/**
 * Campaign Create Page
 * Dedicated page for creating new campaigns
 */

import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeftIcon,
  TicketIcon,
  MegaphoneIcon,
  BellIcon,
} from "@heroicons/react/24/outline";
import { createCampaign } from "../services/campaigns.service";
import EventPicker from "../components/events/EventPicker";
import Button from "../components/common/Button";
import type { CreateCampaignDTO, CampaignType } from "../types/campaign.types";
import { CAMPAIGN_TYPE_OPTIONS } from "../types/campaign.types";

const CampaignCreate: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // Get event_id from URL if coming from an event page
  const preselectedEventId = searchParams.get("event_id");
  const preselectedType = searchParams.get("type") as CampaignType | null;

  const [formData, setFormData] = useState<CreateCampaignDTO>({
    name: "",
    description: "",
    start_date: "",
    end_date: "",
    campaign_type: preselectedType || "marketing",
    event_id: preselectedEventId || undefined,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEventCampaign =
    formData.campaign_type === "event_ticket" ||
    formData.campaign_type === "event_reminder";

  const createMutation = useMutation({
    mutationFn: createCampaign,
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      navigate(`/campaigns/${campaign.id}`);
    },
    onError: (error: Error) => {
      console.error("Error creating campaign:", error);
      setErrors({ submit: error.message || "Failed to create campaign" });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Campaign name is required";
    }

    if (isEventCampaign && !formData.event_id) {
      newErrors.event_id = "Please select an event for this campaign type";
    }

    if (formData.start_date && formData.end_date) {
      if (new Date(formData.start_date) > new Date(formData.end_date)) {
        newErrors.end_date = "End date must be after start date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const submitData: CreateCampaignDTO = {
      name: formData.name.trim(),
      description: formData.description?.trim() || undefined,
      campaign_type: formData.campaign_type,
      event_id: isEventCampaign ? formData.event_id : undefined,
      start_date: formData.start_date || undefined,
      end_date: formData.end_date || undefined,
    };

    createMutation.mutate(submitData);
  };

  const handleCancel = () => {
    navigate("/campaigns");
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center space-x-4">
        <button
          onClick={handleCancel}
          className="text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          Create New Campaign
        </h1>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Campaign Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Campaign Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {CAMPAIGN_TYPE_OPTIONS.map((option) => {
                const isSelected = formData.campaign_type === option.value;
                const Icon =
                  option.value === "marketing"
                    ? MegaphoneIcon
                    : option.value === "event_ticket"
                      ? TicketIcon
                      : BellIcon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        campaign_type: option.value,
                        event_id: undefined,
                      })
                    }
                    className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? "border-primary-500 bg-primary-50 ring-2 ring-primary-200"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`p-2 rounded-lg ${isSelected ? "bg-primary-100" : "bg-gray-100"}`}
                      >
                        <Icon
                          className={`h-5 w-5 ${isSelected ? "text-primary-600" : "text-gray-500"}`}
                        />
                      </div>
                      <div>
                        <div
                          className={`font-medium ${isSelected ? "text-primary-900" : "text-gray-900"}`}
                        >
                          {option.label}
                        </div>
                        <div
                          className={`text-sm ${isSelected ? "text-primary-600" : "text-gray-500"}`}
                        >
                          {option.description}
                        </div>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <svg
                          className="h-5 w-5 text-primary-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Event Selection (for event-based campaigns) */}
          {isEventCampaign && (
            <div>
              <label
                htmlFor="event_id"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Select Event <span className="text-red-500">*</span>
              </label>
              <EventPicker
                inputId="event_id"
                testId="campaign-event-picker"
                value={formData.event_id || ""}
                onChange={(id) =>
                  setFormData({
                    ...formData,
                    event_id: id || undefined,
                  })
                }
                required
              />
              {errors.event_id && (
                <p className="mt-1 text-sm text-red-600">{errors.event_id}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                {formData.campaign_type === "event_ticket"
                  ? "The campaign will automatically target registered attendees for this event."
                  : "Reminder will be sent to attendees of the selected event."}
              </p>
            </div>
          )}

          {/* Campaign Name */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Campaign Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.name ? "border-red-500" : "border-gray-300"
              }`}
              placeholder={
                isEventCampaign
                  ? "Event Ticket Distribution"
                  : "Holiday Promotion 2025"
              }
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Describe the campaign goals and strategy..."
            />
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="start_date"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Start Date
              </label>
              <input
                type="date"
                id="start_date"
                value={formData.start_date}
                onChange={(e) =>
                  setFormData({ ...formData, start_date: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label
                htmlFor="end_date"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                End Date
              </label>
              <input
                type="date"
                id="end_date"
                value={formData.end_date}
                onChange={(e) =>
                  setFormData({ ...formData, end_date: e.target.value })
                }
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.end_date ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.end_date && (
                <p className="mt-1 text-sm text-red-600">{errors.end_date}</p>
              )}
            </div>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800">{errors.submit}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={createMutation.isPending}
              className="flex items-center gap-2"
            >
              {createMutation.isPending && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              Create Campaign
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CampaignCreate;
